/**
 * Muidrawer.js - Layout aligned with D:\ForBuild\admin4.0\src\Shell\Muidrawer.js:
 * Box eMed_muiDrawer, AppBar with Toolbar (profile, Avatar menu), Drawer, RouterComponent.
 * Uses CacheProviderService (sessionStorage: getCachevalue / clearCachevalue).
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import CssBaseline from "@mui/material/CssBaseline";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";
import GridViewIcon from "@mui/icons-material/GridView";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import WarningIcon from "@mui/icons-material/Warning";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import { getCachevalue, clearCachevalue } from "../Utility/Services/CacheProviderService";
import { getSideMenuTree } from "../Utility/SideMenuList";
import RouterComponent from "../Utility/RouterComponent";
import { Colors } from "../Styles/Colors";
import { APP_Version_Label, APP_Support_Label } from "../Utility/Services/RestAPIService";
import ImagePaths from "../Utility/ImagePaths";
import "./NavbarApp.css";

const drawerWidth = "13vw";
const drawerPaddingLeft = 0; /* no left spacing on drawer head */

const contentInTooltip = (str, len) =>
  str && str.length > len ? `${str.slice(0, len)}...` : str || "";
const t = (key) => key;

const openedMixin = (theme) => ({
  width: drawerWidth,
  minWidth: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  overflowY: "auto",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  overflowY: "auto",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: 0,
  minHeight: "4.1vw",
  boxShadow: "none",
  border: "none",
}));

const AppBar = styled(MuiAppBar, { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth})`,
      transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  })
);

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(open && {
      ...openedMixin(theme),
      "& .MuiDrawer-paper": {
        ...openedMixin(theme),
        paddingLeft: drawerPaddingLeft,
        borderRight: "none",
        "&::after": {
          content: '""',
          position: "absolute",
          top: "4.1vw",
          right: 0,
          bottom: 0,
          width: "1px",
          backgroundColor: "lightgray",
        },
      },
    }),
    ...(!open && {
      ...closedMixin(theme),
      "& .MuiDrawer-paper": {
        ...closedMixin(theme),
        paddingLeft: drawerPaddingLeft,
        borderRight: "none",
        "&::after": {
          content: '""',
          position: "absolute",
          top: "4.1vw",
          right: 0,
          bottom: 0,
          width: "1px",
          backgroundColor: "lightgray",
        },
      },
    }),
  })
);

export default function Muidrawer() {
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [loginName, setLoginName] = useState("");
  const [idleAlertPopup, setIdleAlertPopup] = useState(false);
  const idleTimerRef = useRef(null);
  const idleAlertTimerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isLoggedIn = getCachevalue("isLoggedIn");
  const menuTree = isLoggedIn ? getSideMenuTree(getCachevalue) : [];
  const myUser = JSON.parse(getCachevalue("loggedInUserInfo") || "{}");

  const [expandedIds, setExpandedIds] = useState([]);
  const menuIdsKey = (menuTree?.map((p) => p.id) ?? []).join(",");

  useEffect(() => {
    if (menuTree?.length) {
      setExpandedIds(menuTree.map((p) => p.id));
    }
  }, [menuIdsKey]);
  const DisableSwitchModule = false;
  const isDashboard = location.pathname === "/HistopathologyHome";

  useEffect(() => {
    if (myUser?.username) setLoginName(myUser.username);
  }, [myUser?.username]);

  const handleCloseUserMenu = () => setAnchorElUser(null);

  const clearAccessCache = () => {};
  const cacheKeysToClear = [
    "isCanteenOverall", "IsPORequestEdit", "Laboratory_id", "subInbound", "subOutbound",
    "branchInbound", "branchOutbound", "subTotal", "branchTotal", "LabStatusDateFilter",
    "LabCancelledDateFilter", "LabStatusDateCustumFilter", "LabTstReportDateFilter",
    "LabForTechDateFilter", "SelectedOPDate", "SelectedDate", "OP_CreateAppointment_PatientId",
    "BillType", "FromOtherScreen", "Status", "FormDate", "ToDate",
  ];

  const handleSwitchModule = () => {
    handleCloseUserMenu();
    setLoginName("");
    setAnchorElUser(null);
    clearCachevalue("AccessToken");
    clearCachevalue("loggedInUserInfo");
    clearCachevalue("clinicId");
    clearCachevalue("is_investigation_module");
    clearCachevalue("investigation_name");
    cacheKeysToClear.forEach((k) => clearCachevalue(k));
    clearAccessCache();
    navigate("/HistopathologyHome");
  };

  const logout = useCallback(() => {
    clearCachevalue("AccessToken");
    clearCachevalue("loggedInUserInfo");
    clearCachevalue("isLoggedIn");
    clearCachevalue("LoginAuthToken");
    clearCachevalue("LogedUserName");
    clearCachevalue("loggedUserId");
    clearCachevalue("BranchId");
    clearCachevalue("BranchName");
    clearCachevalue("auto_log_out_time");
    clearCachevalue("Scrren_access");
    clearCachevalue("clinicId");
    cacheKeysToClear.forEach((k) => clearCachevalue(k));
    clearAccessCache();
    navigate("/");
  }, [navigate]);

  const clearTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (idleAlertTimerRef.current) clearTimeout(idleAlertTimerRef.current);
    idleTimerRef.current = null;
    idleAlertTimerRef.current = null;
  }, []);

  const autoLogOutOnIdle = useCallback(() => {
    if (location.pathname === "/") {
      clearTimer();
    } else {
      clearTimer();
      logout();
    }
  }, [location.pathname, clearTimer, logout]);

  const startTimer = useCallback(() => {
    setIdleAlertPopup(false);
    const raw = getCachevalue("auto_log_out_time");
    const customLogoutTime = raw ? Number(raw) : 15;
    const logoutMs = customLogoutTime * 60 * 1000;
    const alertMs = (customLogoutTime - 3) * 60 * 1000;
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (idleAlertTimerRef.current) clearTimeout(idleAlertTimerRef.current);
    idleTimerRef.current = setTimeout(autoLogOutOnIdle, logoutMs);
    idleAlertTimerRef.current = setTimeout(() => setIdleAlertPopup(true), Math.max(0, alertMs));
  }, [autoLogOutOnIdle]);

  const resetIdleTimer = useCallback(() => {
    clearTimer();
    startTimer();
  }, [clearTimer, startTimer]);

  useEffect(() => {
    if (!isLoggedIn) return;
    startTimer();
    const events = ["mousemove", "mousedown", "touchstart", "touchmove", "click", "keydown"];
    const onActivity = () => resetIdleTimer();
    events.forEach((ev) => window.addEventListener(ev, onActivity));
    window.addEventListener("scroll", onActivity, true);
    return () => {
      clearTimer();
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      window.removeEventListener("scroll", onActivity, true);
    };
  }, [isLoggedIn, startTimer, resetIdleTimer, clearTimer]);

  const profileMenuDisabled =
    !(location.pathname === "/NoAccess") &&
    (getCachevalue("isForCancelBill") === "true" ||
      getCachevalue("IsForCheckedOut") === "true" ||
      getCachevalue("isInRadiologyBilling") === "true" ||
      getCachevalue("DisableAllActions") === "true");
  const avatarDisabled = profileMenuDisabled || location.pathname === "/video-call";

  const isParentActive = (parent) =>
    parent.children?.some((c) => location.pathname === c.path || location.pathname.startsWith(c.path + "/"));
  const isChildActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  const toggleExpand = useCallback((id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  if (!isLoggedIn) {
    return <RouterComponent />;
  }

  return (
    <div style={{ height: "100vh", maxHeight: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box className="eMed_muiDrawer" sx={{ flex: 1, display: "flex", minHeight: 0 }}>
        <CssBaseline />
        <AppBar sx={{ minHeight: "4.1vw", boxShadow: "none" }} position="fixed" open={true} elevation={0}>
          <Toolbar>
            <div className="fieldHeaderfordoctor">
              {isDashboard ? (
                <div className="eMed_profile">
                  <div className="eMed_user">
                    <label className="eMed_loginuser">{getCachevalue("LogedUserName")}</label>
                  </div>
                </div>
              ) : (
                <div className="eMed_profile">
                  <div className="eMed_user">
                    <label className="eMed_loginuser">{loginName || myUser?.username || getCachevalue("LogedUserName")}</label>
                  </div>
                  <div className="eMed_servicetype">
                    <label className="eMed_LoginType">Histopathology</label>
                  </div>
                </div>
              )}
              <div className="eMed_Avatar">
                <Box>
                  <Tooltip title="Profile" arrow>
                    <IconButton
                      type="button"
                      disabled={avatarDisabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        setAnchorElUser(e.currentTarget);
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: Colors.IconCircleBgColor,
                          boxShadow: 3,
                          color: Colors.ThemeColor,
                        }}
                        id="eMed_Icons_Avatar"
                      />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    id="eMed_profileMenu"
                    anchorEl={anchorElUser}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    keepMounted
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                    sx={{ marginRight: "5vw" }}
                    TransitionProps={{ timeout: 0 }}
                    disableAutoFocus
                    disableRestoreFocus
                    MenuListProps={{ onClick: (e) => e.stopPropagation() }}
                  >
                    <div className="eMed_AvatarMenu">
                      {DisableSwitchModule ? null : !location.pathname.includes("/HistopathologyHome") ? (
                        <MenuItem
                          onClick={handleSwitchModule}
                        >
                          <GridViewIcon sx={{ color: Colors.ThemeColor, marginRight: "0.5vw" }} />
                          {t("SwitchModule")}
                        </MenuItem>
                      ) : null}
                      <MenuItem
                        onClick={() => {
                          handleCloseUserMenu();
                          logout();
                        }}
                      >
                        <ExitToAppIcon sx={{ color: Colors.ThemeColor, marginRight: "0.5vw" }} />
                        {t("Logout")} / <span>{contentInTooltip(getCachevalue("LogedUserName"), 15)}</span>
                      </MenuItem>
                    </div>
                  </Menu>
                </Box>
              </div>
            </div>
          </Toolbar>
        </AppBar>

        {location.pathname !== "/video-call" && (
          <Drawer className="eMed_Drawer" variant="permanent" open={true} sx={{ flexShrink: 0 }}>
            <DrawerHeader sx={{ backgroundColor: Colors.ThemeColor, justifyContent: "center", px: 1 }}>
              {/* <Box component="img" src={ImagePaths.LogoWhite?.default} alt="eMedHub" sx={{ height: "4vw", width: "auto", display: "block" }} /> */}
            </DrawerHeader>
            <List id="eMed_side_list" className="eMed_side_list_tree" disablePadding sx={{ pl: "0.5vw", pr: 0.15, pt: 0, pb: "7vh" }}>
              {menuTree.map((parent) => {
                const isOpen = expandedIds.includes(parent.id);
                const parentActive = isParentActive(parent);
                return (
                  <Box key={parent.id}>
                    <ListItemButton
                      className="eMed_menuItems eMed_tree_parent"
                      onClick={() => {
                        if (parent.children?.length) toggleExpand(parent.id);
                        else navigate(parent.path);
                      }}
                      sx={{
                        borderRadius: 0.75,
                        py: 0.2,
                        px: 0.25,
                        mb: 0.08,
                        lineHeight: 1.2,
                        backgroundColor: parentActive && !isOpen ? Colors.treeActiveBg || "rgba(0,0,0,0.06)" : "transparent",
                        "&:hover": { backgroundColor: Colors.treeActiveBg || "rgba(0,0,0,0.04)" },
                      }}
                      disableRipple
                    >
                      <ListItemIcon sx={{ minWidth: "1.75vw", pl: 0.5 }}>
                        <Avatar
                          src={parentActive ? parent.OnIcon?.default : parent.OffIcon?.default}
                          sx={{ width: "1.35vw", height: "1.35vw" }}
                          variant="square"
                        />
                      </ListItemIcon>
                      <>
                          <ListItemText
                            primary={parent.label}
                            primaryTypographyProps={{
                              fontSize: "0.4dvw",
                              fontWeight: parentActive ? 600 : 500,
                              color: parentActive ? Colors.themeDark : "black",
                              noWrap: false,
                              lineHeight: 1.2,
                            }}
                            sx={{ flex: 1, minWidth: 0 }}
                          />
                          {isOpen ? <ExpandLess sx={{ color: Colors.ListIcons, fontSize: "1rem" }} /> : <ExpandMore sx={{ color: Colors.ListIcons, fontSize: "1rem" }} />}
                        </>
                    </ListItemButton>
                    <Collapse in={isOpen} timeout={150} unmountOnExit>
                      <List component="div" disablePadding sx={{ pl: 1.25 }}>
                        {parent.children?.map((child) => {
                          const childActive = isChildActive(child.path);
                          return (
                            <ListItem key={child.path} disablePadding>
                              <ListItemButton
                                disableRipple
                                onClick={() => navigate(child.path)}
                                sx={{
                                  borderRadius: 0.75,
                                  py: 0.18,
                                  pl: 0.75,
                                  lineHeight: 1.2,
                                  backgroundColor: childActive ? Colors.ThemeColor : "transparent",
                                  color: childActive ? Colors.white : "inherit",
                                  "&:hover": {
                                    backgroundColor: childActive ? Colors.ThemeColor : "rgba(0,0,0,0.04)",
                                    color: childActive ? Colors.white : undefined,
                                  },
                                }}
                              >
                                <ListItemText
                                  primary={child.menuName}
                                  primaryTypographyProps={{
                                    fontSize: "0.4dvw",
                                    fontWeight: childActive ? 600 : 400,
                                    noWrap: false,
                                    lineHeight: 1.2,
                                  }}
                                  sx={{ minWidth: 0 }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </Box>
                );
              })}
            </List>
          </Drawer>
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            width: "100%",
            maxWidth: "100%",
            // pl: "1.5vw",
            pr: "1vw",
            pt: 0,
            pb: "6vh",
            overflow: "hidden",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <RouterComponent />
        </Box>
      </Box>
      <Box sx={{ height: "1vh", width: "100%", zIndex: 1200, position: "fixed", bottom: "5vh", borderTop: "1px solid lightgrey", backgroundColor: Colors.BgColor }} />
      <Box sx={{ height: "5vh", width: "100%", zIndex: 1200, position: "fixed", bottom: 0, backgroundColor: Colors.ThemeColor, alignItems: "center", justifyContent: "center", display: "flex", flexDirection: "row" }}>
        <Typography color={Colors.white} fontSize="1dvw">www.emedhub.in</Typography>
        <Typography sx={{ paddingLeft: 2, paddingRight: 5 }} color={Colors.white} fontSize="1dvw">{APP_Version_Label}</Typography>
        <Typography color={Colors.white} fontSize="1dvw">{APP_Support_Label}</Typography>
      </Box>
      {idleAlertPopup ? (
        <Box className="IdleNotiContainer">
          <Paper>
            <div className="IdleNotiWrapper">
              <div className="NotiHeaderContainer">
                <div className="NotiIcon">
                  <WarningIcon sx={{ height: "1.5vw", width: "1.5vw", color: "white" }} />
                </div>
                <div className="NotiText" style={{ fontWeight: 600 }}>System Logout Alert!</div>
              </div>
              <div className="NotiPara" style={{ paddingLeft: "10vw" }}>
                Your session will expire in few minutes due to inactivity. Please continue your activity to stay logged in.
              </div>
            </div>
          </Paper>
        </Box>
      ) : null}
    </div>
  );
}
